import { Box, Button, Checkbox, FormControlLabel, MenuItem, Select } from '@mui/material'
import { useSnackbar } from 'notistack'
import { useState } from 'react'
import { DEFAULT, PAYMENT_OPTION_LABELS } from '../constants'
import {
  type PaymentOption,
  type RequeuePaymentsMutationVariables,
  useRequeuePaymentsMutation,
} from '../generated/graphql'
import { ConfirmDialog } from './ConfirmDialog'

interface RequeuePaymentButtonProps {
  onSuccess: () => void
  selectedPaymentIds: number[]
}

export default function RequeuePaymentButton({ onSuccess, selectedPaymentIds }: RequeuePaymentButtonProps) {
  const { enqueueSnackbar } = useSnackbar()
  const [paymentOption, setPaymentOption] = useState(DEFAULT)
  const [waiveFee, setWaiveFee] = useState(false)
  const [confirmOpen, setConfirmOpen] = useState(false)

  const requeuePaymentsMutation = useRequeuePaymentsMutation({
    onSuccess: () => {
      enqueueSnackbar('Payments requeued successfully', { variant: 'success' })
      onSuccess()
    },
    onError: (error: Error) => {
      console.error(error)
      enqueueSnackbar(error?.message || 'Failed to requeue payments', {
        variant: 'error',
      })
    },
  })

  const handleRequeuePayment = (option: string, waiveTransactionFee: boolean) => {
    const variables: RequeuePaymentsMutationVariables = {
      paymentIds: selectedPaymentIds,
      paymentOption: option === DEFAULT ? null : (option as PaymentOption),
      waiveTransactionFee:
        waiveTransactionFee || ['ACH_STANDARD', 'ACH_EXPEDITED', 'ACH_NEXT_DAY', 'ACH_SAME_DAY'].includes(option),
    }
    requeuePaymentsMutation.mutate(variables)
  }

  const count = selectedPaymentIds.length

  return (
    <Box
      sx={{
        mb: 2,
        display: 'flex',
        gap: 2,
        alignItems: 'center',
        flexWrap: 'wrap',
      }}
    >
      <Button
        variant="outlined"
        color="primary"
        onClick={() => setConfirmOpen(true)}
        disabled={requeuePaymentsMutation.isPending || !selectedPaymentIds.length}
      >
        Re-Queue ({selectedPaymentIds.length}) Payments
      </Button>
      <Select
        value={paymentOption}
        onChange={e => setPaymentOption(e.target.value)}
        size="small"
        sx={{ minWidth: 250 }}
      >
        {Object.keys(PAYMENT_OPTION_LABELS).map(value => (
          <MenuItem key={value} value={value}>
            {PAYMENT_OPTION_LABELS[value]}
          </MenuItem>
        ))}
      </Select>
      <FormControlLabel
        control={<Checkbox checked={waiveFee} onChange={e => setWaiveFee(e.target.checked)} color="primary" />}
        label="Waive fee"
      />

      <ConfirmDialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        confirmLabel="Re-Queue Payments"
        onConfirm={() => handleRequeuePayment(paymentOption, waiveFee)}
      >
        You are about to re-queue {count} selected payment(s) for processing.
        <Box component="span" sx={{ display: 'block', mt: 1.5 }}>
          Speed: <strong>{PAYMENT_OPTION_LABELS[paymentOption] ?? paymentOption}</strong>
        </Box>
      </ConfirmDialog>
    </Box>
  )
}
